import React, { useEffect } from 'react'
import { Control, Controller, ControllerProps, FieldValues } from 'react-hook-form'

import { INPUT_TYPES } from '@/constants/utils'
import Field from '@/components/Field'
import Select from '@/components/Select'
import OTPField from '@/components/OTPField'
import TagField from '@/components/TagField'
import Checkbox from '@/components/Checkbox'
import PhoneField from '@/components/PhoneField'
import ColorField from '@/components/ColorField'
import MultiSelect from '@/components/MultiSelect'
import FileField from '@/components/Field/FileField'
import DualColorField from '@/components/DualColorField'
import DateRangeField from '@/components/DateRangeField'
import UserSelectField from '@/components/UserSelectField'
import MultiValueField from '@/components/MultiValueField'
import StepperField from '@/components/Field/StepperField'
import ContactSelector from '@/components/ContactSelector'
import VCardSelectField from '@/components/VCardSelectField'
import TimezoneSelectField from '@/components/TimezoneSelectField'

const INPUT_COMPONENTS = {
  [INPUT_TYPES.FIELD]: Field,
  [INPUT_TYPES.COLOR]: ColorField,
  [INPUT_TYPES.DUAL_COLOR]: DualColorField,
  [INPUT_TYPES.FILE]: FileField,
  [INPUT_TYPES.SELECT]: Select,
  [INPUT_TYPES.MULTI_SELECT]: MultiSelect,
  [INPUT_TYPES.USER_SELECT]: UserSelectField,
  [INPUT_TYPES.OTP]: OTPField,
  [INPUT_TYPES.PHONE_FIELD]: PhoneField,
  [INPUT_TYPES.TAGS]: TagField,
  [INPUT_TYPES.STEPPER]: StepperField,
  [INPUT_TYPES.CHECK_BOX]: Checkbox,
  [INPUT_TYPES.MULTI_VALUE_FIELD]: MultiValueField,
  [INPUT_TYPES.DATE_RANGE]: DateRangeField,
  [INPUT_TYPES.TIMEZONE_SELECT]: TimezoneSelectField,
  [INPUT_TYPES.VCARD_MULTI_SELECT_FIELD]: VCardSelectField,
  [INPUT_TYPES.CONTACT_SELECTOR]: ContactSelector,
}

// helper to get component props
type ComponentProps<T> = T extends React.ComponentType<infer P> ? P : never

// props injected by RHF we don't want user to pass
type ControlledKeys = 'value' | 'onChange' | 'onBlur' | 'error'

// omit controlled props
type ComponentControlledProps<T> = Omit<ComponentProps<T>, ControlledKeys>

// map each INPUT_TYPES to its safe component props
type InputComponentProps = {
  [K in keyof typeof INPUT_COMPONENTS]: ComponentControlledProps<(typeof INPUT_COMPONENTS)[K]>
}

// final props

type Props<T extends FieldValues, K extends keyof typeof INPUT_COMPONENTS> = Omit<
  ControllerProps<T>,
  'render' | 'control'
> & {
  componentKey: K
  hideControllerField?: boolean
  control: Control<T, any, any>
} & InputComponentProps[K]

function ControllerField<T extends FieldValues, K extends keyof typeof INPUT_COMPONENTS>({
  componentKey,
  hideControllerField = false,
  control,
  name,
  defaultValue,
  rules,
  ...rest
}: Props<T, K>) {
  const Component = INPUT_COMPONENTS[componentKey]

  useEffect(() => {
    if (hideControllerField) {
      control.unregister(name)
    } else {
      control.register(name)
    }
  }, [hideControllerField, control, name])

  if (hideControllerField) {
    return null
  }

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={rules}
      render={({ field, fieldState }) => {
        // @ts-ignore
        return <Component error={fieldState.error} {...field} {...rest} />
      }}
    />
  )
}

export default ControllerField
